import { z } from 'zod';

const name = z.string().trim().min(1).max(80);
export const lang = z.enum(['sv', 'en']);
export const role = z.enum(['tenant', 'reception', 'admin']);
const email = z
  .email()
  .max(254)
  .transform((v) => v.trim().toLowerCase());

export const inviteUserInput = z
  .object({
    email,
    firstName: name,
    lastName: name,
    role: role.default('tenant'),
    companyId: z.uuid().nullable().default(null),
    language: lang.default('sv'),
  })
  .refine((v) => (v.role === 'tenant') === (v.companyId !== null), {
    message: 'Tenants need a company; staff have none',
    path: ['companyId'],
  });
export type InviteUserInput = z.infer<typeof inviteUserInput>;

export const updateUserInput = z.object({
  firstName: name.optional(),
  lastName: name.optional(),
  companyId: z.uuid().optional(),
  language: lang.optional(),
});

export const changeEmailInput = z.object({ email });

export const updateMeInput = z.object({
  firstName: name.optional(),
  lastName: name.optional(),
  language: lang.optional(),
  reminderMinutes: z.number().int().min(5).max(1440).nullable().optional(),
});

export const acceptInviteInput = z.object({ password: z.string().min(1).max(256) });

const isoInstant = z.iso.datetime({ offset: true }).transform((v) => new Date(v));

export const repeatInput = z
  .object({
    until: z.iso.date().optional(),
    count: z.number().int().min(2).max(52).optional(),
  })
  .refine((v) => (v.until === undefined) !== (v.count === undefined), {
    message: 'Give either until or count',
  });

export const createBookingInput = z.object({
  roomId: z.uuid(),
  start: isoInstant,
  end: isoInstant,
  title: z.string().trim().max(120).default(''),
  attendees: z.number().int().min(1).max(500),
  /** Staff only: book on behalf of this user. */
  userId: z.string().min(1).optional(),
  /** Staff only: accept attendees above capacity. */
  overrideCapacity: z.boolean().default(false),
  repeat: repeatInput.optional(),
});
export type CreateBookingInput = z.infer<typeof createBookingInput>;

export const scope = z.enum(['one', 'following']);

export const updateBookingInput = z.object({
  roomId: z.uuid().optional(),
  start: isoInstant.optional(),
  end: isoInstant.optional(),
  title: z.string().trim().max(120).optional(),
  attendees: z.number().int().min(1).max(500).optional(),
  overrideCapacity: z.boolean().default(false),
  scope: scope.default('one'),
});
export type UpdateBookingInput = z.infer<typeof updateBookingInput>;

export const cancelBookingInput = z.object({
  scope: scope.default('one'),
  reason: z.string().trim().max(300).optional(),
});

export const rangeQuery = z.object({ from: isoInstant, to: isoInstant });

export const findRoomQuery = z.object({
  start: isoInstant,
  end: isoInstant,
  attendees: z.coerce.number().int().min(1).default(1),
});

export const blockInput = z.object({
  roomIds: z.array(z.uuid()).min(1),
  start: isoInstant,
  end: isoInstant,
  reason: z.string().trim().max(200).default(''),
});

export const roomInput = z.object({
  name: z.string().trim().min(1).max(60),
  floor: z.number().int().min(-5).max(200),
  capacity: z.number().int().min(1).max(1000),
  equipment: z.string().trim().max(500).default(''),
  layout: z.string().trim().max(200).default(''),
  description: z.string().trim().max(2000).default(''),
  active: z.boolean().default(true),
  accessGroupIds: z.array(z.uuid()).default([]),
});

export const companyInput = z.object({
  name: z.string().trim().min(1).max(120),
  accessGroupIds: z.array(z.uuid()).default([]),
});

export const accessGroupInput = z.object({ name: z.string().trim().min(1).max(60) });

export const SETTING_KEYS = [
  'booking.max_minutes',
  'booking.max_days_ahead',
  'reminder.default_minutes',
  'retention.booking_months',
  'retention.deactivated_months',
  'retention.audit_months',
] as const;
export type SettingKey = (typeof SETTING_KEYS)[number];

export const settingsInput = z.partialRecord(
  z.enum(SETTING_KEYS),
  z.number().int().min(0).max(100_000),
);

export const brandingInput = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  primaryColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
});
